<template>
  <div class="verify-email">
    <div class="verify-email__card">
      <div
        v-if="isLoading"
        class="verify-email__loading"
      >
        <div class="verify-email__spinner"></div>
        <p class="verify-email__text">Weryfikacja adresu email...</p>
      </div>
      <div
        v-else-if="error"
        class="verify-email__error"
      >
        <div class="verify-email__icon verify-email__icon--error">
          <svg
            class="verify-email__svg"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h2 class="verify-email__subtitle">Weryfikacja nie powiodła się</h2>
        <p class="verify-email__message">{{ error }}</p>
        <button
          class="verify-email__button"
          @click="retryVerification"
        >
          Spróbuj ponownie
        </button>
      </div>
      <div
        v-else
        class="verify-email__success"
      >
        <div class="verify-email__icon verify-email__icon--success">
          <svg
            class="verify-email__svg"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h2 class="verify-email__subtitle">Email zweryfikowany pomyślnie!</h2>
        <p class="verify-email__message">
          Twój adres email został zweryfikowany. Możesz się teraz zalogować do swojego konta.
        </p>
        <router-link
          to="/login"
          class="verify-email__button"
        >
          Przejdź do logowania
        </router-link>
      </div>
      <div class="verify-email__footer">
        <img
          src="@/assets/logo.png"
          alt="Logo"
          class="verify-email__logo"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { UserService } from '@/api/user/userService';

const route = useRoute();
const isLoading = ref(true);
const error = ref<string | null>(null);

const verifyEmail = async () => {
  try {
    const token = route.query['token'] as string;
    if (!token) {
      throw new Error('Nie znaleziono tokenu weryfikacyjnego. Sprawdź link w swojej wiadomości email.');
    }

    const response = await fetch(`${UserService['apiBaseUrl']}/users/verify-email?token=${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Weryfikacja email nie powiodła się. Spróbuj ponownie później.');
    }
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : 'Wystąpił błąd podczas weryfikacji email. Spróbuj ponownie później.';
  } finally {
    isLoading.value = false;
  }
};

const retryVerification = () => {
  isLoading.value = true;
  error.value = null;
  verifyEmail();
};

onMounted(() => {
  verifyEmail();
});
</script>

<style lang="scss" scoped>
@use 'sass:color';

.verify-email {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, $white-100 0%, $brown-400 100%);
  padding: 20px;
}

.verify-email__card {
  background: $white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba($black, 0.1);
  width: 100%;
  max-width: 480px;
  padding: 48px;
}

.verify-email__loading,
.verify-email__error,
.verify-email__success {
  text-align: center;
}

.verify-email__spinner {
  width: 4rem;
  height: 4rem;
  border: 3px solid $brown-200;
  border-top-color: transparent;
  border-radius: 50%;
  margin: 0 auto;
  animation: spin 1s linear infinite;
}

.verify-email__text {
  margin-top: 1.5rem;
  color: $black-400;
  font-size: 2.5rem;
  font-weight: 500;
}

.verify-email__icon {
  margin-bottom: 2rem;

  &--error &__svg {
    color: #e53e3e;
  }

  &--success &__svg {
    color: #48bb78;
  }
}

.verify-email__svg {
  width: 4rem;
  height: 4rem;
}

.verify-email__subtitle {
  font-size: 2rem;
  font-weight: 600;
  color: $black-300;
  margin-bottom: 1.5rem;
}

.verify-email__message {
  color: $black-400;
  font-size: 1.6875rem;
  line-height: 1.5;
  margin-bottom: 2rem;
}

.verify-email__button {
  display: inline-block;
  padding: 1.1rem 2.2rem;
  background-color: $brown-200;
  color: $white;
  border-radius: 0.5rem;
  text-decoration: none;
  transition: background-color 0.2s;
  font-size: 2.0625rem;
  font-weight: 500;
  border: none;
  cursor: pointer;

  &:hover {
    background: color.adjust($brown-200, $lightness: -5%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba($brown-200, 0.3);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.verify-email__footer {
  text-align: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba($black, 0.1);
}

.verify-email__logo {
  height: 60px;
  width: auto;
}
</style>
